import { AutoPaymentCollectionTimingEnum } from '@monetize/types/app';
import { useEffect } from 'react';
import {
  useConfigurePaymentCollectAutoConfig,
  useGetPaymentCollectAutoConfig,
} from '../../../api/settingsService';
import {
  MPageContainer,
  MSettingsPageHeader,
  MVStack,
} from '../../../components/Monetize';
import { useAsyncStatus } from '../../../hooks/useAsyncStatus';
import {
  getHighlightStyles,
  useSettingsSectionHighlight,
} from '../../../hooks/useSettingsSectionHighlight';
import { useDocumentHead } from '../../../services/documentHead';
import { useFlags } from '../../../services/launchDarkly';
import { SettingsSection } from '../components/SettingsSection';
import { SettingsSelect } from '../components/SettingsSelect';
import { SettingsToggle } from '../components/SettingsToggle';

const AUTO_PAYMENT_TIMING_ITEMS = [
  {
    value: AutoPaymentCollectionTimingEnum.INVOICE_GENERATION,
    label: 'Invoice Generation',
  },
  {
    value: AutoPaymentCollectionTimingEnum.INVOICE_SEND,
    label: 'Invoice Send',
  },
];

export const Payments = () => {
  const { enableAutoPaymentTiming } = useFlags();
  const { setDocTitle } = useDocumentHead();
  const isCollectHighlighted = useSettingsSectionHighlight(
    'collectAutomatically',
  );
  const isTimingHighlighted = useSettingsSectionHighlight('autoPaymentTiming');
  const isPartialHighlighted = useSettingsSectionHighlight(
    'allowPartialPayments',
  );
  const { data: paymentCollectConfig, isLoading: fetchLoading } =
    useGetPaymentCollectAutoConfig();
  const { mutateAsync: updatePaymentCollectAutoConfig } =
    useConfigurePaymentCollectAutoConfig();

  const { status: collectStatus, track: trackCollect } = useAsyncStatus();
  const { status: partialStatus, track: trackPartial } = useAsyncStatus();
  const { status: timingStatus, track: trackTiming } = useAsyncStatus();

  useEffect(() => {
    setDocTitle('Settings', 'Payments');
  }, []);

  return (
    <MPageContainer alignItems="stretch">
      <MSettingsPageHeader title="Payments" />

      <MVStack maxW="600px" gap={4}>
        <SettingsSection
          id="collectAutomatically"
          {...getHighlightStyles(isCollectHighlighted)}
        >
          <SettingsToggle
            name="collectAutomaticallyToggle"
            heading="Collect Automatically"
            subheading="This will set the default for the payment collection behavior for new Bill Groups"
            fetchLoading={fetchLoading}
            value={paymentCollectConfig?.collectAutomatically ?? false}
            asyncStatus={collectStatus}
            onChange={(collectAutomatically) =>
              trackCollect(async () => {
                if (!paymentCollectConfig) {
                  return;
                }
                await updatePaymentCollectAutoConfig({
                  ...paymentCollectConfig,
                  collectAutomatically,
                });
              })
            }
          />
        </SettingsSection>
        {enableAutoPaymentTiming && (
          <SettingsSection
            id="autoPaymentTiming"
            {...getHighlightStyles(isTimingHighlighted)}
          >
            <SettingsSelect
              name="autoPaymentCollectionTiming"
              heading="Auto-Payment Timing"
              subheading="When to collect payment automatically — at invoice generation or after the invoice is sent"
              fetchLoading={fetchLoading}
              value={
                paymentCollectConfig?.autoPaymentCollectionTiming ??
                AutoPaymentCollectionTimingEnum.INVOICE_GENERATION
              }
              items={AUTO_PAYMENT_TIMING_ITEMS}
              isDisabled={!paymentCollectConfig?.collectAutomatically}
              asyncStatus={timingStatus}
              onChange={(autoPaymentCollectionTiming) =>
                trackTiming(async () => {
                  if (!paymentCollectConfig) {
                    return;
                  }
                  await updatePaymentCollectAutoConfig({
                    ...paymentCollectConfig,
                    autoPaymentCollectionTiming:
                      autoPaymentCollectionTiming as AutoPaymentCollectionTimingEnum,
                  });
                })
              }
            />
          </SettingsSection>
        )}

        <SettingsSection
          id="allowPartialPayments"
          {...getHighlightStyles(isPartialHighlighted)}
          showDivider={false}
        >
          <SettingsToggle
            name="allowPartialPayment"
            heading="Allow Partial Payments"
            subheading="Allow customers to make partial payments for individual Invoices"
            fetchLoading={fetchLoading}
            value={paymentCollectConfig?.allowPartialPayment ?? false}
            asyncStatus={partialStatus}
            onChange={(allowPartialPayment) =>
              trackPartial(async () => {
                if (!paymentCollectConfig) {
                  return;
                }
                await updatePaymentCollectAutoConfig({
                  ...paymentCollectConfig,
                  allowPartialPayment,
                });
              })
            }
          />
        </SettingsSection>
      </MVStack>
    </MPageContainer>
  );
};
